/** @prettier */

import {useState, useEffect} from 'react';
import {listTimeZones} from 'timezone-support';
import {Container, Box, Stack, Grid} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import FindLocation from './FindLocation';
import ComboBox from '../../component/AutoCompleteBox';
import TextFieldSelectBox from '../../component/SelectBox';
import DialogPage from '../../component/DialogMessage';
import {PrimaryButton} from '../../component/BasicButton';
//API import
import {postCompositionsSites} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {routeList} from '../PrivateRoute';
import {format, pullDownMenu} from '../../define';

/**
 * 新しいサイトを追加する
 * @returns JSX
 */
const AddSite = () => {
  const trans = useTranslation().t; //翻訳関数

  //prettier-ignore
  //サイト名
  const [siteName, setSiteName] = useState<apiIF.reqParameters.postCompositionsSites['site-name']>('');
  //prettier-ignore
  //市
  const [city, setCity] = useState<apiIF.reqParameters.postCompositionsSites['city']>('');
  //prettier-ignore
  //天気予報用地域
  const [location, setLocation] = useState<apiIF.reqParameters.postCompositionsSites['location']>('');
  //prettier-ignore
  //タイムゾーン
  const [timezone, setTimezone] = useState<apiIF.reqParameters.postCompositionsSites['timezone'] | null>(null);
  //prettier-ignore
  //地上階
  const [aboveground, setAboveground] = useState<apiIF.reqParameters.postCompositionsSites['above-ground']>('0');
  //prettier-ignore
  //地下階
  const [underground, setUnderground] = useState<apiIF.reqParameters.postCompositionsSites['under-ground']>('0');
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false); //SAVEボタン非表示フラグ
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ

  //TextBoxのエラー状態
  const [siteNameError, setSiteNameError] = useState(false); //サイト名
  const [cityError, setCityError] = useState(false); //市

  const timeZoneList = listTimeZones(); //タイムゾーン一覧

  //入力項目のチェック
  useEffect(() => {
    if (
      siteName === '' ||
      city === '' ||
      location === '' ||
      timezone === null ||
      siteNameError ||
      cityError
    ) {
      setSaveBtnDisabled(true);
    } else {
      setSaveBtnDisabled(false);
    }
  }, [siteName, city, location, timezone, siteNameError, cityError]);

  /**
   * SAVEボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };
  /**
   * SAVEボタン出現処理
   * @returns JSX
   */
  const SaveButton = () => {
    //追加するサイト情報（APIに渡すJSONデータ）
    const addSiteData: apiIF.reqParameters.postCompositionsSites = {
      'site-name': siteName,
      'city': city,
      'location': location,
      'timezone': timezone === null ? '' : timezone,
      'above-ground': aboveground,
      'under-ground': underground,
    };

    return (
      <>
        {/* 入力必須箇所に空欄が有る場合はADDボタンをDisabled */}
        <PrimaryButton
          onClick={handleClickOpen}
          buttonText={trans('AddSite.buttonTxt.save')}
          id="mv-btnAddSiteSave"
          disabled={saveBtnDisabled}
        />
        {/* 入力必須箇所に空欄が有る場合は、警告文を表示 */}
        {saveBtnDisabled ? (
          <Grid container>
            <Grid item xs={4} className="siteSetting__text--error">
              {trans('AddSite.errMsg.inputErr')}
            </Grid>
          </Grid>
        ) : (
          <></>
        )}
        {/* ADD押下時、Dialogで実行の最終確認をする */}
        <DialogPage
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          titleString={trans('AddSite.dialogBox.title')}
          contentText={trans('AddSite.dialogBox.msg')}
          buttonNameLeft={trans('AddSite.dialogBox.buttonTxt.cancel')}
          buttonNameRight={trans('AddSite.dialogBox.buttonTxt.ok')}
          execFunc={() => postCompositionsSites(addSiteData)}
          redirectPath={routeList.mySite.path}
          id="mv-addSite"
        />
      </>
    );
  };

  return (
    <Container fixed className="siteSetting">
      <Grid container spacing={3} justifyContent={'center'}>
        {/** タイトル、説明 */}
        <Grid item xs={12}>
          <Box>
            <h1>{trans('AddSite.title')}</h1>
          </Box>
          <Box className="siteSetting__text--required">
            {trans('AddSite.note')}
          </Box>
        </Grid>
        {/** 場所の情報 */}
        <Grid item xs={12} container rowSpacing={1}>
          {/** タイトル */}
          <Grid item xs={12}>
            <h2>{trans('AddSite.heading.placeInfo')}</h2>
          </Grid>
          <Grid item xs={12} className="siteSetting__contents__item">
            <Stack spacing={1}>
              {/** サイトの名前 */}
              <ChkInputTextbox
                setValue={setSiteName}
                setError={setSiteNameError}
                chkLength={format.BASIC_LENGTH_MAX}
                type="text"
                label={`${trans('AddSite.txtField.label.siteName')}*`}
                id="mv-inputSiteName"
                class="siteSetting__textBox--freeItem"
                helperText=" "
                errHelperText={trans('AddSite.txtField.errHelperTxt')}
                variant="standard"
                autoFocus={true}
                chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
              />
              {/** 市 */}
              <ChkInputTextbox
                setValue={setCity}
                setError={setCityError}
                chkLength={format.BASIC_LENGTH_MAX}
                type="text"
                label={`${trans('AddSite.txtField.label.city')}*`}
                id="mv-inputCityName"
                class="siteSetting__textBox--freeItem"
                helperText=" "
                errHelperText={trans('AddSite.txtField.errHelperTxt')}
                variant="standard"
              />
              {/** 天気予報用地域 */}
              <FindLocation
                id="mv-editLocationName"
                class="siteSetting__textBox--pulldown"
                setLocation={setLocation}
              />
              {/** タイムゾーン */}
              <ComboBox
                data={timeZoneList}
                setValue={setTimezone}
                value={timezone}
                id="mv-selectTimezone"
                class="siteSetting__textBox--pulldown"
              />
            </Stack>
          </Grid>
        </Grid>
        {/** 階層の情報 */}
        <Grid item xs={12} container>
          {/** タイトル */}
          <Grid item xs={12}>
            <h2>{trans('AddSite.heading.buildingInfo')}</h2>
          </Grid>
          {/** 階層 */}
          <Grid item xs={12} className="siteSetting__contents__item">
            <Grid container columnSpacing={2}>
              {/** タイトル */}
              <Grid item xs="auto">
                <h3>{trans('AddSite.subhead.buildingInfo')}</h3>
              </Grid>
              <Grid item xs>
                <Stack spacing={1}>
                  {/** 地上階 */}
                  <TextFieldSelectBox
                    label={`${trans('AddSite.txtField.label.aboveground')}*`}
                    choices={pullDownMenu.ABOVEGROUND_FLOOR}
                    value={aboveground}
                    setValue={setAboveground}
                    id="mv-selectAbovegnd"
                    class="siteSetting__textBox--pulldownFloor"
                  />
                  {/** 地下階 */}
                  <TextFieldSelectBox
                    label={`${trans('AddSite.txtField.label.basement')}*`}
                    choices={pullDownMenu.UNDERGROUND_FLOOR}
                    value={underground}
                    setValue={setUnderground}
                    id="mv-selectUndergnd"
                    class="siteSetting__textBox--pulldownFloor"
                  />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/** 保存ボタン */}
        <Grid item xs={12} className="siteSetting__button">
          <SaveButton />
        </Grid>
      </Grid>
    </Container>
  );
};
export default AddSite;
